<template>
  <div class="page-container">
    <el-tabs v-model="activeTab" class="product-h5-tabs" @tab-change="handleTabChange">
      <!-- Tab 1: 产品暗展配置 -->
      <el-tab-pane label="产品暗展配置" name="config">
        <div class="tab-inner">
          <div class="fixed-header">
            <div>
              <el-button type="primary" :disabled="loading" @click="openDialog(null)">新建配置</el-button>
            </div>
            <p class="hint">
              客户端拉取：<code>GET /api/v2/passthrough/h5-config?productId=&amp;versionCount=</code>
              （返回 <code>h5_ads_open</code> + <code>h5_ads_config</code>）
            </p>
          </div>

          <div class="table-wrapper">
            <el-table v-loading="loading" :data="data" height="calc(100vh - 250px)">
              <el-table-column label="名称" width="140" prop="name" show-overflow-tooltip />
              <el-table-column label="product_id" min-width="180" prop="productId" show-overflow-tooltip />
              <el-table-column label="version_count" width="130" prop="versionCount" />
              <el-table-column label="h5_ads_open" width="120">
                <template #default="scope">
                  <el-switch
                    :model-value="scope.row.h5AdsOpen > 0"
                    :active-value="true"
                    :inactive-value="false"
                    :disabled="loading"
                    @change="(val: boolean) => toggleH5AdsOpen(scope.row, val)"
                  />
                </template>
              </el-table-column>
              <el-table-column label="h5_ads_config" min-width="160">
                <template #default="scope">
                  <el-button type="primary" link @click="openJsonDialog(scope.row)">
                    编辑 JSON
                  </el-button>
                  <span class="json-summary">{{ jsonSummary(scope.row.h5AdsConfig) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="160" fixed="right">
                <template #default="scope">
                  <el-button size="small" :disabled="loading" @click="openDialog(scope.row)">编辑</el-button>
                  <el-button size="small" type="danger" :disabled="loading" @click="remove(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="fixed-pagination">
            <div style="display: flex; justify-content: space-between">
              <el-pagination
                layout="prev, pager, next"
                :total="total"
                :page-size="query.limit || 50"
                @current-change="pageChange"
                :disabled="loading"
              />
              <el-select v-model="query.limit" style="width: 100px" @change="reload" :disabled="loading">
                <el-option :value="20" />
                <el-option :value="50" />
                <el-option :value="100" />
              </el-select>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab 2: 暗展统计 -->
      <el-tab-pane label="暗展统计" name="stats">
        <div class="tab-inner">
          <div class="stats-header">
            <div class="stats-actions">
              <el-button type="primary" :disabled="statsLoading" @click="openStatsDialog(null)">
                新建统计
              </el-button>
              <el-button :disabled="statsLoading" @click="initStats">刷新数据</el-button>
              <el-input
                v-model="statsQuery.productId"
                placeholder="按 product_id 筛选"
                clearable
                style="width: 220px"
                @change="reloadStats"
                @clear="reloadStats"
              />
            </div>
            <div class="stats-banner">
              <el-alert type="info" :closable="false" show-icon>
                <template #title>
                  <span class="banner-title">
                    按时间周期滚动统计：首条数据进入设定时间起点，超时后自动重置清零并开启新周期。
                  </span>
                </template>
                <div class="banner-body">
                  <div>
                    <strong>获取实时统计：</strong><code>GET /api/v2/passthrough/h5-stats?productId=&amp;versionCount=</code>
                    （无需 key，已超期时返回 0，未超期返回当前窗口实时 <code>data</code>）
                  </div>
                  <div style="margin-top: 4px;">
                    <strong>递增指标接口：</strong><code>POST /api/v2/passthrough/h5-stats/increment</code>
                    （需带 <code>key</code> 鉴权，参数含 <code>field</code> / <code>fields</code> 与可选 <code>delta</code>）
                  </div>
                </div>
              </el-alert>
            </div>
          </div>

          <div class="table-wrapper">
            <el-table v-loading="statsLoading" :data="statsData" height="calc(100vh - 290px)">
              <el-table-column label="名称" width="130" prop="name" show-overflow-tooltip />
              <el-table-column label="product_id" min-width="160" prop="productId" show-overflow-tooltip />
              <el-table-column label="version_count" width="115" prop="versionCount" />
              <el-table-column label="统计周期" width="125">
                <template #default="scope">
                  <el-tag size="small" type="primary" effect="plain" style="cursor: pointer;" @click="openEditWindowDialog(scope.row)">
                    {{ formatWindowSeconds(scope.row.windowSeconds) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="当前周期与状态" min-width="210">
                <template #default="scope">
                  <div class="window-status-cell">
                    <template v-if="!scope.row.windowStartAt">
                      <el-tag size="small" type="info">待首条数据启动</el-tag>
                    </template>
                    <template v-else-if="scope.row.isExpired">
                      <el-tag size="small" type="danger">已超期(待重置)</el-tag>
                      <span class="status-tip">上次起点: {{ formatSimpleTime(scope.row.windowStartAt) }}</span>
                    </template>
                    <template v-else>
                      <el-tag size="small" type="success">生效中</el-tag>
                      <span class="status-tip">
                        {{ formatSimpleTime(scope.row.windowStartAt) }} (余{{ formatRemaining(scope.row.remainingSeconds) }})
                      </span>
                    </template>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="密钥 (key)" min-width="180">
                <template #default="scope">
                  <div class="key-cell">
                    <span class="key-code" :title="scope.row.key">
                      {{ showKeys[scope.row.id] ? scope.row.key : maskKey(scope.row.key) }}
                    </span>
                    <el-button
                      link
                      type="primary"
                      size="small"
                      @click="toggleShowKey(scope.row.id)"
                    >
                      {{ showKeys[scope.row.id] ? '隐藏' : '显示' }}
                    </el-button>
                    <el-button
                      link
                      type="primary"
                      size="small"
                      @click="copyText(scope.row.key)"
                    >
                      复制
                    </el-button>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="统计数据 (data)" min-width="260">
                <template #default="scope">
                  <div v-if="scope.row.data && Object.keys(scope.row.data).length > 0" class="data-tag-container">
                    <el-tag
                      v-for="(val, field) in scope.row.data"
                      :key="field"
                      class="data-tag"
                      type="info"
                      effect="plain"
                      closable
                      @close="handleRemoveField(scope.row, String(field))"
                    >
                      <span class="tag-key">{{ field }}:</span>
                      <strong class="tag-val">{{ val }}</strong>
                    </el-tag>
                  </div>
                  <span v-else class="empty-data-hint">暂无字段，请在右侧点击「添加字段」</span>
                </template>
              </el-table-column>
              <el-table-column label="更新时间" width="155" prop="updatedAt" show-overflow-tooltip />
              <el-table-column label="操作" width="310" fixed="right">
                <template #default="scope">
                  <el-button size="small" type="primary" link @click="openAddFieldDialog(scope.row)">
                    添加字段
                  </el-button>
                  <el-button size="small" type="primary" link @click="openEditWindowDialog(scope.row)">
                    设周期
                  </el-button>
                  <el-button size="small" type="success" link @click="openApiDocDialog(scope.row)">
                    API示例
                  </el-button>
                  <el-button size="small" type="warning" link @click="handleResetWindow(scope.row)">
                    重置周期
                  </el-button>
                  <el-button size="small" type="warning" link @click="handleResetKey(scope.row)">
                    重置Key
                  </el-button>
                  <el-button size="small" type="danger" link @click="removeStats(scope.row)">
                    删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="fixed-pagination">
            <div style="display: flex; justify-content: space-between">
              <el-pagination
                layout="prev, pager, next"
                :total="statsTotal"
                :page-size="statsQuery.limit || 50"
                @current-change="statsPageChange"
                :disabled="statsLoading"
              />
              <el-select v-model="statsQuery.limit" style="width: 100px" @change="reloadStats" :disabled="statsLoading">
                <el-option :value="20" />
                <el-option :value="50" />
                <el-option :value="100" />
              </el-select>
            </div>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Tab 1 对话框：新建 / 编辑配置 -->
    <el-dialog v-model="dialogOpened" :title="editing ? '编辑配置' : '新建配置'" :before-close="resetDialog">
      <el-form :model="form" label-width="140px">
        <el-form-item label="名称" required>
          <el-input v-model="form.name" placeholder="展示用名称，如 Jigsaw iOS" />
        </el-form-item>
        <el-form-item label="product_id" required>
          <el-input v-model="form.productId" placeholder="com.neptune.jigsaw" />
        </el-form-item>
        <el-form-item label="version_count" required>
          <el-input-number v-model="form.versionCount" :min="1" :max="999999" />
        </el-form-item>
        <el-form-item label="h5_ads_open">
          <el-radio-group v-model="form.h5AdsOpen">
            <el-radio :label="0">0（关）</el-radio>
            <el-radio :label="1">1（开）</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="loading" text @click="resetDialog">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>

    <!-- Tab 1 对话框：编辑 h5_ads_config JSON -->
    <el-dialog
      v-model="jsonDialogOpened"
      :title="jsonEditing ? `h5_ads_config — ${jsonEditing.name}` : 'h5_ads_config'"
      width="720px"
      :before-close="resetJsonDialog"
    >
      <p class="hint dialog-hint">
        编辑 <code>h5_ads_config</code> 对象（不含 <code>h5_ads_open</code>），与暗展 H5 配置页一致。
      </p>
      <el-input
        v-model="jsonForm.h5AdsConfigJson"
        type="textarea"
        :rows="22"
        placeholder="编辑 h5_ads_config 对象"
        class="json-editor"
      />
      <div class="toolbar">
        <el-button size="small" @click="formatJson" :disabled="loading">格式化 JSON</el-button>
        <el-button size="small" @click="resetTemplate" :disabled="loading">恢复默认模板</el-button>
      </div>
      <template #footer>
        <el-button type="primary" :loading="loading" text @click="resetJsonDialog">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submitJson">保存</el-button>
      </template>
    </el-dialog>

    <!-- Tab 2 对话框：新建统计 -->
    <el-dialog v-model="statsDialogOpened" title="新建暗展统计" width="560px" :before-close="resetStatsDialog">
      <el-form :model="statsForm" label-width="140px">
        <el-form-item label="名称" required>
          <el-input v-model="statsForm.name" placeholder="展示用名称，如 Jigsaw iOS 统计" />
        </el-form-item>
        <el-form-item label="product_id" required>
          <el-input v-model="statsForm.productId" placeholder="com.neptune.jigsaw" />
        </el-form-item>
        <el-form-item label="version_count" required>
          <el-input-number v-model="statsForm.versionCount" :min="1" :max="999999" />
        </el-form-item>
        <el-form-item label="密钥 (key)">
          <el-input
            v-model="statsForm.key"
            placeholder="留空将自动生成 32 位安全随机 Key"
            clearable
          />
        </el-form-item>
        <el-form-item label="初始自定义字段">
          <el-input
            v-model="statsForm.initialFieldsText"
            placeholder="英文逗号分隔，如 pv, ad_click, game_start（初始值均为 0）"
            clearable
          />
        </el-form-item>
        <el-form-item label="统计周期">
          <div style="display: flex; flex-direction: column; gap: 8px; width: 100%;">
            <el-radio-group v-model="statsForm.windowSeconds">
              <el-radio-button :value="600">10分钟</el-radio-button>
              <el-radio-button :value="1800">30分钟</el-radio-button>
              <el-radio-button :value="3600">1小时</el-radio-button>
              <el-radio-button :value="7200">2小时</el-radio-button>
              <el-radio-button :value="21600">6小时</el-radio-button>
              <el-radio-button :value="86400">24小时</el-radio-button>
            </el-radio-group>
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 13px; color: var(--el-text-color-secondary);">自定义秒数:</span>
              <el-input-number v-model="statsForm.windowSeconds" :min="10" :max="2592000" :step="60" style="width: 170px;" />
              <span style="font-size: 12px; color: var(--el-text-color-placeholder);">({{ formatWindowSeconds(statsForm.windowSeconds) }})</span>
            </div>
            <span class="hint" style="margin-top: 0;">以第一条数据请求时间为起点，超过此周期后 data 全部自动重置归零</span>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="statsLoading" text @click="resetStatsDialog">取消</el-button>
        <el-button type="primary" :loading="statsLoading" @click="submitStats">确定</el-button>
      </template>
    </el-dialog>

    <!-- Tab 2 对话框：修改统计周期 -->
    <el-dialog
      v-model="editWindowDialogOpened"
      :title="editWindowRow ? `设置统计周期 — ${editWindowRow.name || editWindowRow.productId}` : '设置统计周期'"
      width="540px"
      :before-close="resetEditWindowDialog"
    >
      <el-form label-width="110px">
        <el-form-item label="统计周期">
          <div style="display: flex; flex-direction: column; gap: 8px; width: 100%;">
            <el-radio-group v-model="editWindowForm.windowSeconds">
              <el-radio-button :value="600">10分钟</el-radio-button>
              <el-radio-button :value="1800">30分钟</el-radio-button>
              <el-radio-button :value="3600">1小时</el-radio-button>
              <el-radio-button :value="7200">2小时</el-radio-button>
              <el-radio-button :value="21600">6小时</el-radio-button>
              <el-radio-button :value="86400">24小时</el-radio-button>
            </el-radio-group>
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 13px; color: var(--el-text-color-secondary);">自定义秒数:</span>
              <el-input-number v-model="editWindowForm.windowSeconds" :min="10" :max="2592000" :step="60" style="width: 170px;" />
              <span style="font-size: 12px; color: var(--el-text-color-placeholder);">({{ formatWindowSeconds(editWindowForm.windowSeconds) }})</span>
            </div>
          </div>
        </el-form-item>
        <p class="hint" style="margin-left: 110px; margin-top: -4px;">
          首条数据进入时设定时间起点，后来的请求超过该周期后，data 下数据将全部重置并重新设置时间起点。
        </p>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="statsLoading" text @click="resetEditWindowDialog">取消</el-button>
        <el-button type="primary" :loading="statsLoading" @click="submitEditWindow">保存</el-button>
      </template>
    </el-dialog>

    <!-- Tab 2 对话框：添加自定义字段 -->
    <el-dialog
      v-model="addFieldDialogOpened"
      :title="addFieldRow ? `添加字段 — ${addFieldRow.name || addFieldRow.productId}` : '添加字段'"
      width="460px"
      :before-close="resetAddFieldDialog"
    >
      <el-form label-width="100px" @submit.prevent="submitAddField">
        <el-form-item label="字段名称" required>
          <el-input
            v-model="newFieldName"
            placeholder="仅支持字母、数字、下划线，如 ad_click"
            clearable
            @keyup.enter="submitAddField"
          />
        </el-form-item>
        <p class="hint" style="margin-left: 100px; margin-top: -6px;">
          类型为数字，初始默认为 0。页面不支持修改数值，只能通过 API 接口递增改变。
        </p>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="statsLoading" text @click="resetAddFieldDialog">取消</el-button>
        <el-button type="primary" :loading="statsLoading" @click="submitAddField">添加</el-button>
      </template>
    </el-dialog>

    <!-- Tab 2 对话框：API 调用示例 -->
    <el-dialog
      v-model="apiDocDialogOpened"
      :title="apiDocRow ? `API 调用示例 — ${apiDocRow.name || apiDocRow.productId}` : 'API 调用示例'"
      width="700px"
    >
      <div v-if="apiDocRow" class="api-doc-content">
        <!-- 一、获取统计数据 (GET) -->
        <div class="api-section-header">
          <el-tag type="success" size="small" effect="dark">读接口</el-tag>
          <span class="section-title">获取统计数据 (无需密钥)</span>
        </div>
        <p class="api-desc">
          通过 GET 请求直接读取指定 <code>productId</code> 和 <code>versionCount</code> 下的 <code>data</code> 字典：
        </p>
        <div class="api-meta-block">
          <div><strong>接口地址：</strong><code>GET /api/v2/passthrough/h5-stats</code> 或 <code>GET /api/v2/passthrough/h5-stats/data</code></div>
          <div><strong>请求方法：</strong><code>GET</code></div>
        </div>
        <pre class="curl-box"><code>{{ getFetchDataUrlExample(apiDocRow) }}</code></pre>
        <el-button size="small" type="primary" @click="copyText(getFetchDataUrlExample(apiDocRow))">
          复制获取数据 cURL
        </el-button>

        <!-- 二、递增指标 (POST / GET) -->
        <div class="api-section-header" style="margin-top: 20px;">
          <el-tag type="warning" size="small" effect="dark">写接口</el-tag>
          <span class="section-title">递增指标数据 (需带 key 鉴权)</span>
        </div>
        <p class="api-desc">
          客户端递增统计接口需携带 <code>key</code> 鉴权，只有 key 比对成功才允许递增指标值：
        </p>
        <div class="api-meta-block">
          <div><strong>接口地址：</strong><code>POST /api/v2/passthrough/h5-stats/increment</code></div>
          <div><strong>Content-Type：</strong><code>application/json</code></div>
        </div>

        <div class="code-title">单字段递增示例 (cURL)：</div>
        <pre class="curl-box"><code>{{ getSingleCurlExample(apiDocRow) }}</code></pre>
        <el-button size="small" type="primary" @click="copyText(getSingleCurlExample(apiDocRow))">
          复制单字段递增 cURL
        </el-button>

        <div class="code-title" style="margin-top: 14px;">多字段批量递增示例 (JSON)：</div>
        <pre class="curl-box"><code>{{ getBatchJsonExample(apiDocRow) }}</code></pre>

        <div class="code-title" style="margin-top: 14px;">支持 GET 请求递增（方便测试或打点探针）：</div>
        <pre class="curl-box"><code>{{ getGetUrlExample(apiDocRow) }}</code></pre>
      </div>
      <template #footer>
        <el-button type="primary" @click="apiDocDialogOpened = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  PassthroughProductH5ConfigApi,
  PassthroughProductH5ConfigRow,
  PassthroughProductH5StatsApi,
  PassthroughProductH5StatsRow,
} from '../../api/passthroughApi';

// 默认模板与 jigsaw-ios AppDelegate.updateAbConfig 本地 mock 保持一致
const DEFAULT_H5_ADS_CONFIG = {
  ad_flag: 'google_ads_iframe',
  links: [
    {
      url: 'https://puzzle.rabigame.fun/r_game/normal-adslot-test/mai-doc/index.html',
      rollout_percent: 100,
      ad_flag: '',
      block_keywords: '#,abouts,privacy,service',
      detect_ready_width_optimized_ads: 1,
      click_rate: 100,
      auto_roam_on_no_ad_click_after_auto_alignment: 1,
      auto_roam_on_no_ad_click_after_auto_alignment_seconds: 5,
      pages_per_session: [48, 64],
      single_page_session_rate: 2,
      base_stay_seconds: 20,
      stay_random_factor: [0.85, 1.35],
      max_duration_minutes: 20,
      inactivity_timeout_minutes: 5,
      exit_rate_per_page: 0,
      ads_type: 'normal_dom',
      ad_container_selector: ['#normalAdSlot'],
      clickable_selector: [
        'a,[onclick]:not(span),[role="button"]:not(span),[role="link"]:not(span),[jsaction]:not(span)',
      ],
      max_iframe_depth: 6,
      min_iframe_area: 1000,
      probe_window_seconds: 20,
    },
  ],
};

const DEFAULT_FORM = {
  name: 'Jigsaw iOS',
  productId: 'com.neptune.jigsaw',
  versionCount: 1,
  h5AdsOpen: 1 as 0 | 1,
};

const DEFAULT_STATS_FORM = {
  name: '',
  productId: '',
  versionCount: 1,
  key: '',
  initialFieldsText: '',
  windowSeconds: 3600,
};

export default defineComponent({
  name: 'PassthroughProductH5Config',
  data() {
    return {
      activeTab: 'config',

      // Tab 1: 产品暗展配置状态
      loading: false,
      data: [] as PassthroughProductH5ConfigRow[],
      editing: null as PassthroughProductH5ConfigRow | null,
      dialogOpened: false,
      jsonEditing: null as PassthroughProductH5ConfigRow | null,
      jsonDialogOpened: false,
      total: 0,
      page: 1,
      query: { limit: 50, skip: 0 },
      form: { ...DEFAULT_FORM },
      jsonForm: {
        h5AdsConfigJson: JSON.stringify(DEFAULT_H5_ADS_CONFIG, null, 2),
      },

      // Tab 2: 暗展统计状态
      statsLoading: false,
      statsData: [] as PassthroughProductH5StatsRow[],
      statsTotal: 0,
      statsPage: 1,
      statsQuery: { limit: 50, skip: 0, productId: '' },
      statsDialogOpened: false,
      statsForm: { ...DEFAULT_STATS_FORM },
      showKeys: {} as Record<string, boolean>,

      // 添加字段对话框
      addFieldDialogOpened: false,
      addFieldRow: null as PassthroughProductH5StatsRow | null,
      newFieldName: '',

      // 修改统计周期对话框
      editWindowDialogOpened: false,
      editWindowRow: null as PassthroughProductH5StatsRow | null,
      editWindowForm: {
        windowSeconds: 3600,
      },

      // API 示例对话框
      apiDocDialogOpened: false,
      apiDocRow: null as PassthroughProductH5StatsRow | null,
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    handleTabChange(tabName: string) {
      if (tabName === 'stats' && this.statsData.length === 0) {
        this.initStats();
      }
    },

    // -------------------------------------------------------------
    // Tab 1: 产品暗展配置方法
    // -------------------------------------------------------------
    jsonSummary(cfg: Record<string, unknown> | undefined) {
      if (!cfg || typeof cfg !== 'object') return '—';
      const links = Array.isArray(cfg.links) ? cfg.links.length : 0;
      const flag = cfg.ad_flag || '—';
      return `${flag} · ${links} links`;
    },
    resetTemplate() {
      this.jsonForm.h5AdsConfigJson = JSON.stringify(DEFAULT_H5_ADS_CONFIG, null, 2);
    },
    formatJson() {
      try {
        const obj = JSON.parse(this.jsonForm.h5AdsConfigJson);
        this.jsonForm.h5AdsConfigJson = JSON.stringify(obj, null, 2);
        ElMessage.success('已格式化');
      } catch (e: any) {
        ElMessage.error('JSON 无法解析：' + (e?.message || ''));
      }
    },
    async init() {
      this.loading = true;
      try {
        const result = await PassthroughProductH5ConfigApi.list(this.query.skip, this.query.limit);
        this.data = result.list;
        this.total = result.total;
      } catch (e) {
        ElMessage.error('加载配置失败');
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    pageChange(page: number) {
      this.query.skip = (page - 1) * this.query.limit;
      this.init();
    },
    reload() {
      this.query.skip = 0;
      this.page = 1;
      this.init();
    },
    openDialog(row: PassthroughProductH5ConfigRow | null) {
      this.editing = row;
      if (row) {
        this.form = {
          name: row.name,
          productId: row.productId,
          versionCount: row.versionCount,
          h5AdsOpen: (row.h5AdsOpen ? 1 : 0) as 0 | 1,
        };
      } else {
        this.form = { ...DEFAULT_FORM };
      }
      this.dialogOpened = true;
    },
    resetDialog() {
      this.dialogOpened = false;
      this.editing = null;
    },
    openJsonDialog(row: PassthroughProductH5ConfigRow) {
      this.jsonEditing = row;
      this.jsonForm.h5AdsConfigJson = JSON.stringify(row.h5AdsConfig || {}, null, 2);
      this.jsonDialogOpened = true;
    },
    resetJsonDialog() {
      this.jsonDialogOpened = false;
      this.jsonEditing = null;
    },
    async toggleH5AdsOpen(row: PassthroughProductH5ConfigRow, enabled: boolean) {
      const next = enabled ? 1 : 0;
      const prev = row.h5AdsOpen;
      row.h5AdsOpen = next;
      this.loading = true;
      try {
        const updated = await PassthroughProductH5ConfigApi.update(row.id, { h5AdsOpen: next });
        Object.assign(row, updated);
        ElMessage.success('已更新 h5_ads_open');
      } catch (e: any) {
        row.h5AdsOpen = prev;
        ElMessage.error(e?.response?.data?.message || e?.message || '更新失败');
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.form.name.trim() || !this.form.productId.trim()) {
        ElMessage.warning('请填写名称与 product_id');
        return;
      }
      this.loading = true;
      try {
        if (this.editing) {
          await PassthroughProductH5ConfigApi.update(this.editing.id, {
            name: this.form.name.trim(),
            productId: this.form.productId.trim(),
            versionCount: this.form.versionCount,
            h5AdsOpen: this.form.h5AdsOpen,
          });
          ElMessage.success('已保存');
        } else {
          await PassthroughProductH5ConfigApi.create({
            name: this.form.name.trim(),
            productId: this.form.productId.trim(),
            versionCount: this.form.versionCount,
            h5AdsOpen: this.form.h5AdsOpen,
            h5AdsConfigJson: JSON.stringify(DEFAULT_H5_ADS_CONFIG),
          });
          ElMessage.success('已创建');
        }
        this.resetDialog();
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '操作失败');
      } finally {
        this.loading = false;
      }
    },
    async submitJson() {
      if (!this.jsonEditing) return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(this.jsonForm.h5AdsConfigJson);
      } catch (e: any) {
        ElMessage.error('JSON 无法解析：' + (e?.message || ''));
        return;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        ElMessage.warning('h5_ads_config 须为 JSON 对象');
        return;
      }
      this.loading = true;
      try {
        const updated = await PassthroughProductH5ConfigApi.update(this.jsonEditing.id, {
          h5AdsConfigJson: JSON.stringify(parsed),
        });
        Object.assign(this.jsonEditing, updated);
        this.jsonForm.h5AdsConfigJson = JSON.stringify(updated.h5AdsConfig || parsed, null, 2);
        ElMessage.success('已保存');
        this.resetJsonDialog();
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '保存失败');
      } finally {
        this.loading = false;
      }
    },
    async remove(row: PassthroughProductH5ConfigRow) {
      try {
        await ElMessageBox.confirm(`确定删除「${row.name}」？`, '确认', { type: 'warning' });
      } catch {
        return;
      }
      this.loading = true;
      try {
        await PassthroughProductH5ConfigApi.remove(row.id);
        ElMessage.success('已删除');
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '删除失败');
      } finally {
        this.loading = false;
      }
    },

    // -------------------------------------------------------------
    // Tab 2: 暗展统计方法
    // -------------------------------------------------------------
    maskKey(key: string) {
      if (!key) return '—';
      if (key.length <= 8) return '****';
      return key.slice(0, 4) + '••••••••' + key.slice(-4);
    },
    toggleShowKey(id: string) {
      this.showKeys[id] = !this.showKeys[id];
    },
    async copyText(text: string) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        ElMessage.success('已复制到剪贴板');
      } catch {
        const input = document.createElement('input');
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        ElMessage.success('已复制到剪贴板');
      }
    },
    async initStats() {
      this.statsLoading = true;
      try {
        const result = await PassthroughProductH5StatsApi.list(
          this.statsQuery.skip,
          this.statsQuery.limit,
          this.statsQuery.productId.trim() || undefined
        );
        this.statsData = result.list;
        this.statsTotal = result.total;
      } catch (e) {
        ElMessage.error('加载暗展统计失败');
        console.error(e);
      } finally {
        this.statsLoading = false;
      }
    },
    statsPageChange(page: number) {
      this.statsQuery.skip = (page - 1) * this.statsQuery.limit;
      this.initStats();
    },
    reloadStats() {
      this.statsQuery.skip = 0;
      this.statsPage = 1;
      this.initStats();
    },
    openStatsDialog(prefProduct?: PassthroughProductH5ConfigRow | null) {
      this.statsForm = {
        name: prefProduct ? `${prefProduct.name} 统计` : 'Jigsaw iOS 统计',
        productId: prefProduct ? prefProduct.productId : 'com.neptune.jigsaw',
        versionCount: prefProduct ? prefProduct.versionCount : 1,
        key: '',
        initialFieldsText: 'expose_count, click_count',
        windowSeconds: 3600,
      };
      this.statsDialogOpened = true;
    },
    resetStatsDialog() {
      this.statsDialogOpened = false;
      this.statsForm = { ...DEFAULT_STATS_FORM };
    },
    async submitStats() {
      const name = this.statsForm.name.trim();
      const productId = this.statsForm.productId.trim();
      if (!name || !productId) {
        ElMessage.warning('请填写名称与 product_id');
        return;
      }
      const initialFields = this.statsForm.initialFieldsText
        .split(/[,，\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      this.statsLoading = true;
      try {
        await PassthroughProductH5StatsApi.create({
          name,
          productId,
          versionCount: this.statsForm.versionCount,
          key: this.statsForm.key.trim() || undefined,
          initialFields: initialFields.length > 0 ? initialFields : undefined,
          windowSeconds: this.statsForm.windowSeconds || 3600,
        });
        ElMessage.success('已创建暗展统计记录');
        this.resetStatsDialog();
        await this.initStats();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '创建失败');
      } finally {
        this.statsLoading = false;
      }
    },
    openEditWindowDialog(row: PassthroughProductH5StatsRow) {
      this.editWindowRow = row;
      this.editWindowForm = {
        windowSeconds: row.windowSeconds || 3600,
      };
      this.editWindowDialogOpened = true;
    },
    resetEditWindowDialog() {
      this.editWindowDialogOpened = false;
      this.editWindowRow = null;
      this.editWindowForm = { windowSeconds: 3600 };
    },
    async submitEditWindow() {
      if (!this.editWindowRow) return;
      const winSec = Number(this.editWindowForm.windowSeconds) || 3600;
      this.statsLoading = true;
      try {
        const updated = await PassthroughProductH5StatsApi.update(this.editWindowRow.id, {
          windowSeconds: winSec,
        });
        Object.assign(this.editWindowRow, updated);
        ElMessage.success(`已更新统计周期为 ${this.formatWindowSeconds(winSec)}`);
        this.resetEditWindowDialog();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '更新失败');
      } finally {
        this.statsLoading = false;
      }
    },
    async handleResetWindow(row: PassthroughProductH5StatsRow) {
      try {
        await ElMessageBox.confirm(
          `确定手动重置「${row.name}」的当前周期？所有 data 指标值将清零，并将时间起点重置为当前时间重新统计。`,
          '确认重置统计周期',
          { type: 'warning' }
        );
      } catch {
        return;
      }
      this.statsLoading = true;
      try {
        const updated = await PassthroughProductH5StatsApi.resetWindow(row.id);
        Object.assign(row, updated);
        ElMessage.success('已清零统计数据并重置时间起点');
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '重置失败');
      } finally {
        this.statsLoading = false;
      }
    },
    openAddFieldDialog(row: PassthroughProductH5StatsRow) {
      this.addFieldRow = row;
      this.newFieldName = '';
      this.addFieldDialogOpened = true;
    },
    resetAddFieldDialog() {
      this.addFieldDialogOpened = false;
      this.addFieldRow = null;
      this.newFieldName = '';
    },
    async submitAddField() {
      if (!this.addFieldRow) return;
      const fieldName = this.newFieldName.trim();
      if (!fieldName) {
        ElMessage.warning('请输入字段名称');
        return;
      }
      if (!/^[a-zA-Z0-9_]{1,64}$/.test(fieldName)) {
        ElMessage.warning('字段名仅支持1-64位字母、数字或下划线');
        return;
      }
      this.statsLoading = true;
      try {
        const updated = await PassthroughProductH5StatsApi.addField(this.addFieldRow.id, fieldName);
        Object.assign(this.addFieldRow, updated);
        ElMessage.success(`已添加字段「${fieldName}」，默认值为 0`);
        this.resetAddFieldDialog();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '添加字段失败');
      } finally {
        this.statsLoading = false;
      }
    },
    async handleRemoveField(row: PassthroughProductH5StatsRow, fieldName: string) {
      try {
        await ElMessageBox.confirm(
          `确定从「${row.name}」中移除统计字段「${fieldName}」？当前计数值将被删除。`,
          '确认移除字段',
          { type: 'warning' }
        );
      } catch {
        return;
      }
      this.statsLoading = true;
      try {
        const updated = await PassthroughProductH5StatsApi.removeField(row.id, fieldName);
        Object.assign(row, updated);
        ElMessage.success(`已移除字段「${fieldName}」`);
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '移除字段失败');
      } finally {
        this.statsLoading = false;
      }
    },
    async handleResetKey(row: PassthroughProductH5StatsRow) {
      try {
        await ElMessageBox.confirm(
          `重置密钥后，原 Key 将立即失效，使用旧 Key 的客户端上报将无法通过校验。确定重置「${row.name}」的 Key？`,
          '确认重置 Key',
          { type: 'warning' }
        );
      } catch {
        return;
      }
      this.statsLoading = true;
      try {
        const updated = await PassthroughProductH5StatsApi.resetKey(row.id);
        Object.assign(row, updated);
        ElMessage.success('已重置 Key');
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '重置失败');
      } finally {
        this.statsLoading = false;
      }
    },
    async removeStats(row: PassthroughProductH5StatsRow) {
      try {
        await ElMessageBox.confirm(`确定删除「${row.name}」统计记录？`, '确认', { type: 'warning' });
      } catch {
        return;
      }
      this.statsLoading = true;
      try {
        await PassthroughProductH5StatsApi.remove(row.id);
        ElMessage.success('已删除');
        await this.initStats();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '删除失败');
      } finally {
        this.statsLoading = false;
      }
    },
    openApiDocDialog(row: PassthroughProductH5StatsRow) {
      this.apiDocRow = row;
      this.apiDocDialogOpened = true;
    },
    getFetchDataUrlExample(row: PassthroughProductH5StatsRow): string {
      const origin = window.location.origin;
      return `curl '${origin}/api/v2/passthrough/h5-stats?productId=${encodeURIComponent(
        row.productId
      )}&versionCount=${row.versionCount}'`;
    },
    getSingleCurlExample(row: PassthroughProductH5StatsRow): string {
      const field = (row.data && Object.keys(row.data)[0]) || 'click_count';
      const origin = window.location.origin;
      return `curl -X POST '${origin}/api/v2/passthrough/h5-stats/increment' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "productId": "${row.productId}",
    "versionCount": ${row.versionCount},
    "key": "${row.key}",
    "field": "${field}",
    "delta": 1
  }'`;
    },
    getBatchJsonExample(row: PassthroughProductH5StatsRow): string {
      const fieldsObj: Record<string, number> = {};
      const keys = row.data ? Object.keys(row.data) : [];
      if (keys.length > 0) {
        keys.slice(0, 3).forEach((k) => {
          fieldsObj[k] = 1;
        });
      } else {
        fieldsObj.expose_count = 1;
        fieldsObj.click_count = 1;
      }
      return JSON.stringify(
        {
          productId: row.productId,
          versionCount: row.versionCount,
          key: row.key,
          fields: fieldsObj,
        },
        null,
        2
      );
    },
    getGetUrlExample(row: PassthroughProductH5StatsRow): string {
      const field = (row.data && Object.keys(row.data)[0]) || 'click_count';
      const origin = window.location.origin;
      return `GET ${origin}/api/v2/passthrough/h5-stats/increment?productId=${encodeURIComponent(
        row.productId
      )}&versionCount=${row.versionCount}&key=${encodeURIComponent(row.key)}&field=${encodeURIComponent(
        field
      )}&delta=1`;
    },
    formatWindowSeconds(sec?: number): string {
      const s = Number(sec) || 3600;
      if (s < 60) return `${s}秒`;
      if (s < 3600) {
        const m = Math.floor(s / 60);
        const rem = s % 60;
        return rem ? `${m}分${rem}秒` : `${m}分钟`;
      }
      const h = Math.floor(s / 3600);
      const remM = Math.floor((s % 3600) / 60);
      return remM ? `${h}小时${remM}分` : `${h}小时`;
    },
    formatRemaining(sec?: number): string {
      const s = Math.max(0, Math.floor(Number(sec) || 0));
      if (s <= 0) return '0秒';
      if (s < 60) return `${s}秒`;
      if (s < 3600) {
        const m = Math.floor(s / 60);
        return `${m}分${s % 60}秒`;
      }
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      return `${h}小时${m}分`;
    },
    formatSimpleTime(timeStr?: string | null): string {
      if (!timeStr) return '—';
      try {
        const d = new Date(timeStr);
        if (isNaN(d.getTime())) return timeStr.replace('T', ' ').slice(0, 19);
        const pad = (n: number) => String(n).padStart(2, '0');
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        const hours = pad(d.getHours());
        const mins = pad(d.getMinutes());
        const secs = pad(d.getSeconds());
        return `${month}-${day} ${hours}:${mins}:${secs}`;
      } catch {
        return timeStr;
      }
    },
  },
});
</script>

<style scoped>
.page-container {
  padding: 16px 20px;
}
.product-h5-tabs {
  background-color: transparent;
}
.product-h5-tabs :deep(.el-tabs__header) {
  margin-bottom: 16px;
}
.tab-inner {
  display: flex;
  flex-direction: column;
}
.fixed-header {
  margin-bottom: 12px;
}
.hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.dialog-hint {
  margin-top: 0;
  margin-bottom: 12px;
}
.table-wrapper {
  margin-bottom: 12px;
}
.fixed-pagination {
  padding: 8px 0;
}
.json-summary {
  margin-left: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.json-editor {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
}
.toolbar {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

/* Tab 2 样式 */
.stats-header {
  margin-bottom: 14px;
}
.stats-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.stats-banner {
  margin-bottom: 6px;
}
.banner-title {
  font-weight: 600;
  color: #303133;
}
.banner-body {
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
}
.key-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.key-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  background-color: #f4f4f5;
  padding: 2px 6px;
  border-radius: 4px;
  color: #409eff;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.data-tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.data-tag {
  font-size: 12px;
  padding: 2px 8px;
  display: inline-flex;
  align-items: center;
}
.tag-key {
  color: #606266;
  margin-right: 4px;
}
.tag-val {
  color: #409eff;
}
.empty-data-hint {
  font-size: 12px;
  color: #909399;
}
.api-doc-content {
  font-size: 13px;
  line-height: 1.6;
}
.api-desc {
  margin-bottom: 12px;
  color: #606266;
}
.api-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.api-meta-block {
  background-color: #f8f9fa;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
}
.code-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #303133;
}
.curl-box {
  background-color: #282c34;
  color: #abb2bf;
  padding: 12px;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  overflow-x: auto;
  margin-bottom: 8px;
  white-space: pre-wrap;
  word-break: break-all;
}
.window-status-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.status-tip {
  font-size: 11px;
  color: #909399;
}
</style>
